import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { aiChatService } from '../../../services/aiChatService';
import { supabase } from '../../../lib/supabase';

const AIHybridChatQueue = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('ai');
  const [aiChats, setAiChats] = useState([]);
  const [humanChats, setHumanChats] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [takingOver, setTakingOver] = useState(null);
  const [aiAssistantName, setAiAssistantName] = useState('AI Assistant');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [aiConvs, humanConvs, stats] = await Promise.all([
        aiChatService?.getConversationsByHandler(user?.id, 'ai'),
        aiChatService?.getConversationsByHandler(user?.id, 'human'),
        aiChatService?.getInstituteAnalytics(user?.id)
      ]);
      
      setAiChats(aiConvs);
      setHumanChats(humanConvs);
      setAnalytics(stats);
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAIAssistantName = async () => {
    try {
      const { data: institute } = await supabase
        ?.from('institute_profiles')
        ?.select('ai_assistant_name')
        ?.eq('user_id', user?.id)
        ?.single();
      
      if (institute?.ai_assistant_name) {
        setAiAssistantName(institute?.ai_assistant_name);
      }
    } catch (error) {
      console.error('Error loading AI assistant name:', error);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    loadData();
    loadAIAssistantName();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [user?.id, loadData]);

  const handleTakeover = async (conversationId) => {
    try {
      setTakingOver(conversationId);
      await aiChatService?.takeoverConversation(conversationId);
      await loadData();
      navigate('/chat-interface');
    } catch (error) {
      console.error('Error taking over conversation:', error);
      alert('Failed to take over conversation. Please try again.');
    } finally {
      setTakingOver(null);
    }
  };

  const handleArchive = async (e, chatId) => {
    e?.stopPropagation();
    try {
      setActionLoading(chatId);
      await supabase?.from('chat_conversations')?.update({ status: 'closed' })?.eq('id', chatId);
      if (activeTab === 'ai') {
        setAiChats((prev) => prev?.filter((c) => c?.id !== chatId));
      } else {
        setHumanChats((prev) => prev?.filter((c) => c?.id !== chatId));
      }
    } catch (err) {
      console.error('Error archiving chat:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      setActionLoading(confirmDelete);
      await supabase?.from('chat_messages')?.delete()?.eq('conversation_id', confirmDelete);
      await supabase?.from('chat_conversations')?.delete()?.eq('id', confirmDelete);
      if (activeTab === 'ai') {
        setAiChats((prev) => prev?.filter((c) => c?.id !== confirmDelete));
      } else {
        setHumanChats((prev) => prev?.filter((c) => c?.id !== confirmDelete));
      }
    } catch (err) {
      console.error('Error deleting chat:', err);
    } finally {
      setActionLoading(null);
      setConfirmDelete(null);
    }
  };

  const getLastMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString();
  };

  const activeChats = activeTab === 'ai' ? aiChats : humanChats;

  if (loading && !analytics) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading chat data...</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Bot" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                {aiAssistantName} + Human Chat Monitor
              </h2>
              <p className="text-sm text-muted-foreground">
                <span className="data-text">{aiChats?.length}</span> {aiAssistantName} conversations, 
                <span className="data-text ml-1">{humanChats?.length}</span> human conversations
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="left"
            onClick={() => navigate('/chat-interface')}
          >
            View All
          </Button>
        </div>

        {/* Analytics Summary */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Bot" size={14} color="var(--color-accent)" />
                <p className="text-xs text-muted-foreground">{aiAssistantName} Resolution Rate</p>
              </div>
              <p className="text-xl font-bold text-foreground data-text">
                {analytics?.aiResolutionRate?.toFixed(1)}%
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="TrendingUp" size={14} color="var(--color-success)" />
                <p className="text-xs text-muted-foreground">{aiAssistantName} Resolved</p>
              </div>
              <p className="text-xl font-bold text-foreground data-text">
                {analytics?.aiResolvedCount}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="User" size={14} color="var(--color-primary)" />
                <p className="text-xs text-muted-foreground">Human Resolved</p>
              </div>
              <p className="text-xl font-bold text-foreground data-text">
                {analytics?.humanResolvedCount}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="ArrowUpCircle" size={14} color="var(--color-warning)" />
                <p className="text-xs text-muted-foreground">Escalated</p>
              </div>
              <p className="text-xl font-bold text-foreground data-text">
                {analytics?.escalatedCount}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex">
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
              activeTab === 'ai' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Icon name="Bot" size={16} />
              <span>{aiAssistantName} Conversations</span>
              {aiChats?.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                  {aiChats?.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('human')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-smooth ${
              activeTab === 'human' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Icon name="User" size={16} />
              <span>Human Conversations</span>
              {humanChats?.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-success text-success-foreground text-xs font-semibold">
                  {humanChats?.length}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Chat List */}
      <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
        {activeChats?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon 
              name={activeTab === 'ai' ? 'Bot' : 'User'} 
              size={48} 
              color="var(--color-muted-foreground)" 
              className="mx-auto mb-4" 
            />
            <p className="text-muted-foreground">
              No {activeTab === 'ai' ? aiAssistantName : 'human'} conversations at the moment
            </p>
          </div>
        ) : (
          activeChats?.map((chat) => (
            <div
              key={chat?.id}
              className="p-4 hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={24} color="var(--color-primary)" />
                  </div>
                  {chat?.escalationCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-warning text-warning-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                      {chat?.escalationCount}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground truncate">
                        {chat?.student?.name || 'Unknown Student'}
                      </h3>
                      {activeTab === 'ai' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">
                          <Icon name="Bot" size={10} />
                          {aiAssistantName}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {getLastMessageTime(chat?.lastMessageAt)}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {chat?.student?.email || ''}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Icon name="Bot" size={12} />
                        <span className="data-text">{chat?.aiMessageCount}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        <span className="data-text">{chat?.humanMessageCount}</span>
                      </div>
                      {chat?.handoffReason && (
                        <div className="flex items-center gap-1 text-warning">
                          <Icon name="AlertCircle" size={12} />
                          <span>{chat?.handoffReason?.replace('_', ' ')}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      {/* Archive button */}
                      <button
                        title="Archive chat"
                        disabled={actionLoading === chat?.id}
                        onClick={(e) => handleArchive(e, chat?.id)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-warning hover:bg-warning/10 transition-smooth disabled:opacity-50"
                      >
                        <Icon name="Archive" size={15} />
                      </button>
                      {/* Delete button */}
                      <button
                        title="Delete chat"
                        disabled={actionLoading === chat?.id}
                        onClick={(e) => { e?.stopPropagation(); setConfirmDelete(chat?.id); }}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-smooth disabled:opacity-50"
                      >
                        <Icon name="Trash2" size={15} />
                      </button>
                      {activeTab === 'ai' && (
                        <Button
                          variant="default"
                          size="sm"
                          iconName="UserPlus"
                          iconPosition="left"
                          onClick={() => handleTakeover(chat?.id)}
                          disabled={takingOver === chat?.id}
                          loading={takingOver === chat?.id}
                        >
                          Take Over
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="MessageSquare"
                        iconPosition="left"
                        onClick={() => navigate('/chat-interface')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} color="var(--color-destructive)" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete Chat</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              All messages in this conversation will be permanently deleted. Are you sure you want to continue?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(null)}
                disabled={actionLoading === confirmDelete}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={handleDeleteConfirm}
                loading={actionLoading === confirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIHybridChatQueue;